import { formatCardKey } from '@/ai/utils/helpers'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { DomainModelRole } from '@/constants/cardTypes'

export const examples = {
  workflowDiagram: [
    { role: 'Customer', domainEvent: 'Order Placed' },
    { role: 'Support', domainEvent: 'Issue Resolved' },
    { role: 'Supplier', domainEvent: 'Delivery Scheduled' }
  ],
  commandExampleCreateOrder: [
    { name: 'customerName' },
    { name: 'productName' },
    { name: 'deliveryTime' },
    {
      name: 'deliveryAddress',
      fields: [
        {
          name: 'firstName'
        },
        {
          name: 'lastName'
        },
        {
          name: 'address1'
        },
        {
          name: 'address2'
        },
        {
          name: 'city'
        },
        {
          name: 'countryCode'
        },
        {
          name: 'postalCode'
        },
        {
          name: 'phone'
        }
      ]
    },
    { name: 'quantity' },
    { name: 'comment' }
  ],
  commandExampleDeliverOrder: [
    { name: 'id' },
    { name: 'supplier' },
    { name: 'productName' },
    { name: 'deliveryTime' },
    { name: 'deliveryAddress' },
    { name: 'quantity' },
    { name: 'shippingMethod' }
  ],
  commandExampleAddItemsToCart: [
    { name: 'id' },
    {
      name: 'cartItems',
      array: true,
      fields: [
        { name: 'productId' },
        { name: 'quantity' },
        { name: 'variantId' },
        { name: 'unitPrice' },
        {
          name: 'adjustment',
          fields: [
            { name: 'amount' },
            { name: 'code' },
            { name: 'description' }
          ]
        }
      ]
    }
  ],
  domainEventExampleOrderPlaced: [
    { name: 'orderId' },
    { name: 'customerId' },
    { name: 'totalAmount' },
    { name: 'placedAt' }
  ],
  domainEventExampleItemAddedToCart: [
    { name: 'cartId' },
    {
      name: 'cartItem',
      fields: [
        { name: 'productId' },
        { name: 'quantity' },
        { name: 'unitPrice' }
      ]
    }
  ],
  readModelExampleOrder: [
    { name: 'id' },
    { name: 'customer', indexed: true, description: 'Customer who placed the order, used to filter or group orders.' },
    { name: 'order' },
    { name: 'deliveryLocation', description: 'Where the order should be delivered.' }
  ],
  readModelExampleProduct: [
    { name: 'id' },
    { name: 'productName', indexed: true },
    { name: 'productType', indexed: true, description: 'Category or type of the product, used for filtering.' }
  ],
  readModelExampleCustomer: [
    { name: 'id' },
    { name: 'fullName', indexed: true },
    { name: 'address' },
    { name: 'paymentMethod', description: 'Preferred payment method associated with the customer.' }
  ],
  readModelExampleCart: [
    { name: 'id' },
    {
      name: 'status',
      indexed: true,
      description: 'Current status of the cart (e.g. active, checked-out), used for filtering.'
    },
    {
      name: 'cartItem',
      cardinality: 'one-to-many',
      fields: [
        { name: 'productName' },
        { name: 'quantity' },
        { name: 'unitPrice', description: 'Price per unit at the time the item was added.' },
        {
          name: 'adjustment',
          cardinality: 'one-to-one',
          fields: [
            { name: 'amount' },
            { name: 'code', description: 'Promotion or discount code applied to this line item.' }
          ]
        }
      ]
    },
    { name: 'totalAmount', computed: true, description: 'Sum of line-item prices, computed at read time.' }
  ],
  entityExampleOrder: [
    {
      name: 'id',
      description: 'Unique identifier of the order.',
      exampleData: ['ord-001', 'ord-002', 'ord-003'],
      dataType: 'string'
    },
    {
      name: 'customer',
      description: 'The customer who placed the order.',
      exampleData: ['Object', 'Object', 'Object'],
      dataType: 'object',
      relatedEntity: 'Customer'
    },
    {
      name: 'orderItem',
      description: 'Line items included in this order.',
      exampleData: ['Object', 'Object', 'Object'],
      dataType: 'object',
      relatedEntity: 'Order Item',
      array: true
    },
    {
      name: 'quantity',
      description: 'Total number of items in the order.',
      exampleData: ['2', '5', '1'],
      dataType: 'number'
    },
    {
      name: 'totalPrice',
      description: 'Total monetary amount for the order, in the order currency.',
      exampleData: ['39.98', '249.95', '99.99'],
      dataType: 'number'
    },
    {
      name: 'isPaid',
      description: 'Whether the order has been fully paid.',
      exampleData: ['true', 'false', 'true'],
      dataType: 'boolean'
    },
    {
      name: 'createdAt',
      description: 'Timestamp when the order was created.',
      exampleData: [
        '2024-01-15T10:30:00Z',
        '2024-01-16T14:45:00Z',
        '2024-01-17T09:15:00Z'
      ],
      dataType: 'string'
    },
    {
      name: 'deliveryNotes',
      description: 'Free-form instructions for the courier on how to deliver the order.',
      exampleData: ['Leave at door', 'Call before delivery', 'Ring doorbell'],
      dataType: 'string'
    }
  ],
  entityExampleCustomer: [
    {
      name: 'id',
      description: 'Unique identifier of the customer.',
      exampleData: ['cust-001', 'cust-002', 'cust-003'],
      dataType: 'string'
    },
    {
      name: 'customerName',
      description: 'Full name of the customer as used in communications.',
      exampleData: ['John Doe', 'Jane Smith', 'Bob Wilson'],
      dataType: 'string'
    },
    {
      name: 'age',
      description: 'Age of the customer in years.',
      exampleData: ['25', '32', '45'],
      dataType: 'number'
    },
    {
      name: 'isActive',
      description: 'Whether the customer account is currently active.',
      exampleData: ['true', 'false', 'true'],
      dataType: 'boolean'
    }
  ],
  entityExampleProduct: [
    {
      name: 'id',
      description: 'Unique identifier of the product.',
      exampleData: ['prod-001', 'prod-002', 'prod-003'],
      dataType: 'string'
    },
    {
      name: 'productName',
      description: 'Display name of the product shown to customers.',
      exampleData: ['Wireless Mouse', 'USB Cable', 'Laptop Stand'],
      dataType: 'string'
    },
    {
      name: 'price',
      description: 'Unit price of the product in the catalog currency.',
      exampleData: ['19.99', '49.99', '99.99'],
      dataType: 'number'
    },
    {
      name: 'inStock',
      description: 'Whether the product is currently available for purchase.',
      exampleData: ['true', 'true', 'false'],
      dataType: 'boolean'
    }
  ],
  valueObjectExampleAddress: [
    {
      name: 'street',
      description: 'Street address including house or building number.',
      exampleData: ['123 Maple Ave', '456 Oak St', '789 Pine Rd'],
      dataType: 'string'
    },
    {
      name: 'city',
      description: 'City or locality of the address.',
      exampleData: ['New York', 'Chicago', 'Austin'],
      dataType: 'string'
    },
    {
      name: 'postalCode',
      description: 'Postal or ZIP code of the address.',
      exampleData: ['10001', '60601', '73301'],
      dataType: 'string'
    },
    {
      name: 'country',
      description: 'Country of the address.',
      exampleData: ['USA', 'USA', 'USA'],
      dataType: 'string'
    }
  ],
  exampleCategorizedFilePaths: {
    entityRelatedPaths: [
      'src/models/Order.js',
      'src/domain/user/UserAggregate.java'
    ],
    eventRelatedPaths: [
      'src/application/commands/PlaceOrderCommandHandler.py',
      'src/controllers/OrderController.ts',
      'src/graphql/mutations/CreateItem.js'
    ],
    commandRelatedPaths: [
      'src/controllers/OrderController.js',
      'src/services/UserService.java',
      'src/application/commands/PlaceOrderCommandHandler.py',
      'src/graphql/mutations/CreateItem.js'
    ],
    queryRelatedPaths: [
      'src/queries/ProductQueries.js',
      'src/infrastructure/repositories/CustomerRepository.java',
      'src/views/OrderSummaryView.ts',
      'src/routes/GetOrderSummary.ts',
      'src/controllers/GetOrderSummary.ts',
      'src/graphql/queries/GetItem.js'
    ],
    otherDomainRelatedPaths: []
  }
}

export const workflowFromCodeExample = {
  updatesAndAdditions: [
    {
      role: 'User',
      eventDescription: 'Created order',
      command: {
        name: 'Create order',
        fields: examples.commandExampleCreateOrder
      },
      queries: [
        {
          name: 'Get products list',
          queriedEntity: 'Product',
          cardinality: 'one-to-many',
          fields: examples.readModelExampleOrder
        },
        {
          name: 'Get customer details',
          queriedEntity: 'Customer',
          cardinality: 'one-to-one',
          fields: examples.readModelExampleCustomer
        }
      ],
      entities: [
        {
          name: 'Customer',
          isAggregateRoot: false,
          required: ['id', 'customerName'],
          fields: [
            { name: 'id', dataType: 'string' },
            { name: 'customerName', dataType: 'string' },
            { name: 'customerAddress', dataType: 'string' },
            { name: 'phoneNumber', dataType: 'string' }
          ]
        },
        {
          name: 'Product',
          isAggregateRoot: false,
          required: ['id', 'productName', 'productType'],
          fields: [
            { name: 'id', dataType: 'string' },
            { name: 'productName', dataType: 'string' },
            { name: 'productType', dataType: 'string' },
            { name: 'stockLevel', dataType: 'number' }
          ]
        },
        {
          name: 'Order',
          isAggregateRoot: true,
          required: ['id', 'customerId'],
          fields: [
            { name: 'id', dataType: 'string' },
            {
              name: 'customerId',
              dataType: 'object',
              relatedEntity: 'Customer'
            },
            { name: 'totalAmount', dataType: 'number' },
            { name: 'isPaid', dataType: 'boolean' }
          ]
        }
      ],
      gwt: [
        'Given the customer has selected items, when they create an order, then the order should be saved in the system.'
      ]
    }
  ]
}

export const workflowDataExample = {
  events: [
    {
      id: 'event_1',
      description: 'Example description 1',
      laneId: 'lane_1',
      parentEventNames: ['start'],
      cards: [
        {
          id: 'card_1',
          description: 'Card 1',
          schemaId: 'command_schema_1',
          cardType: {
            id: 'card_type_1',
            title: 'Card type 1'
          }
        },
        {
          id: 'card_2',
          description: 'Card 2',
          schemaId: 'query_schema_1',
          cardType: {
            id: 'card_type_2',
            title: 'Card type 2'
          }
        }
      ]
    },
    {
      id: 'event_2',
      description: 'Example description 2',
      laneId: 'lane_2',
      parentEventNames: ['Example description 1'],
      cards: [
        {
          id: 'card_3',
          description: 'Card 3',
          schemaId: 'command_schema_2',
          cardType: {
            id: 'card_type_1',
            title: 'Card type 1'
          }
        },
        {
          id: 'card_4',
          description: 'Card 4',
          schemaId: 'query_schema_2',
          cardType: {
            id: 'card_type_2',
            title: 'Card type 2'
          }
        }
      ]
    }
  ],
  lanes: [
    { id: 'lane_1', name: 'Lane 1' },
    { id: 'lane_2', name: 'Lane 2' }
  ],
  schemas: [
    {
      id: 'entity_1',
      name: 'Entity 1',
      type: 'Entity',
      fields: [
        {
          name: 'Field 1',
          cardinality: 'one-to-one',
          exampleData: ['Example', 'Example', 'Example'],
          dataType: 'string',
          relatedEntityId: null,
          isRequired: true
        },
        {
          name: 'Field 2',
          cardinality: 'one-to-one',
          exampleData: ['Example', 'Example', 'Example'],
          dataType: 'object',
          relatedEntityId: 'entity_2',
          isRequired: false
        }
      ]
    },
    {
      id: 'entity_2',
      name: 'Entity 2',
      type: 'Entity',
      fields: [
        {
          name: 'Field 1',
          cardinality: 'one-to-one',
          exampleData: ['Example', 'Example', 'Example'],
          dataType: 'string',
          relatedEntityId: null,
          isRequired: true
        },
        {
          name: 'Field 2',
          cardinality: 'one-to-many',
          exampleData: ['Example', 'Example', 'Example'],
          dataType: 'object',
          relatedEntityId: 'entity_3',
          isRequired: false
        }
      ]
    },
    {
      id: 'command_schema_1',
      name: 'Write model',
      type: 'Command',
      fields: [
        {
          name: 'Field 1',
          cardinality: 'one-to-one',
          exampleData: ['Example data', 'Example data', 'Example data'],
          relatedEntityId: 'entity_1',
          isRequired: true
        },
        {
          name: 'Field 2',
          cardinality: null,
          exampleData: null,
          relatedEntityId: null,
          isRequired: false
        }
      ]
    },
    {
      id: 'query_schema_1',
      name: 'Read Model',
      type: 'Query',
      entityId: 'entity_1',
      fields: [
        {
          name: 'Field 1',
          cardinality: null,
          exampleData: ['Example data', 'Example data', 'Example data'],
          relatedEntityId: 'entity_1'
        },
        {
          name: 'Field 2',
          cardinality: null,
          exampleData: null,
          relatedEntityId: null
        }
      ]
    },
    {
      id: 'command_schema_2',
      name: 'Write model 2',
      type: 'Command',
      fields: []
    },
    {
      id: 'query_schema_2',
      name: 'Read Model 2',
      type: 'Query',
      entityId: 'entity_2',
      fields: []
    }
  ]
}

export const exampleFileTagsOutput = {
  fileTags: [
    {
      path: 'src/controllers/OrderController.js',
      tags: ['command-uuid1', 'readmodel-uuid2']
    },
    {
      path: 'src/models/Customer.js',
      tags: ['entity-uuid3']
    },
    {
      path: 'src/services/ProductQueryService.java',
      tags: ['readmodel-uuid4']
    }
  ]
}

export const workflowDescriptionExamples = [
  'The incident management process for ITIL version 3',
  'A manufacturing process for outdoor clothing',
  'An order process used by e-commerce platform similar to Amazon',
  'A process for last mile delivery for an electronics retailer',
  'A KYC process for onboarding a large corporate bank customer',
  'A process for ordering home delivery of food',
  'A recruitment process for engineers used by Google',
  'A CRM process used by Salesforce'
]

export const getEventExample = (exampleData, cardTypes, options = {}) => {
  const {
    role,
    name,
    commandExample,
    commandRequired,
    readModelName,
    readModelExample,
    readModelDescription,
    queriedEntity,
    cardinality,
    domainEventExample,
    domainEventEntity,
    id,
    conditionLabel
  } = exampleData
  const { shouldAddCards = true, shouldAddSchemas = true } = options
  let exampleObj
  if (id) {
    // used when adding to an existing event (with known id)
    exampleObj = { id }
  } else {
    // used when generating a new event (without known id)
    exampleObj = { role, domainEventName: name, conditionLabel }
  }

  const commandCardType = diagramHelpers.methods.getCommandCardType(cardTypes)
  const queryCardType = diagramHelpers.methods.getQueryCardType(cardTypes)

  if (shouldAddSchemas && commandExample && commandCardType?.generateWithAI) {
    if (commandRequired) {
      exampleObj.required = commandRequired
    }
    exampleObj.fields = commandExample
  }

  if (shouldAddSchemas && readModelExample && queryCardType?.generateWithAI) {
    if (readModelName) {
      exampleObj.readModelName = readModelName
    }
    exampleObj.readModelFields = readModelExample
    exampleObj.queriedEntity = queriedEntity
    exampleObj.cardinality = cardinality || 'one-to-many'
    if (readModelDescription) {
      exampleObj.readModelDescription = readModelDescription
    }
  }

  if (shouldAddSchemas && domainEventExample) {
    exampleObj.domainEventFields = domainEventExample
    exampleObj.domainEventEntity = domainEventEntity
  }

  if (shouldAddCards && cardTypes?.length) {
    cardTypes.forEach((cardType) => {
      if (cardType.generateWithAI && cardType.title) {
        const key = formatCardKey(cardType.title)
        if (cardType.domainModelRole === DomainModelRole.TAG) {
          exampleObj[key] = ['example-tag-1', 'example-tag-2', 'example-tag-3']
        } else if (cardType.domainModelRole === DomainModelRole.GIVEN_WHEN_THEN) {
          exampleObj[key] = [
            `Example ${cardType.title.toLowerCase()} 1`,
            `Example ${cardType.title.toLowerCase()} 2`
          ]
        } else {
          exampleObj[
            key
          ] = `Example ${cardType.title.toLowerCase()} X`
        }
      }
    })
  }

  return exampleObj
}
